import { useState } from "react";
import { ArrowLeft, MapPin, Clock, Star, Heart, MessageCircle, Share2, ShoppingCart, User, Verified, Award, Calendar, Euro, Package, Truck, RefreshCw, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import LocationPreview from "@/components/LocationPreview";
import MessageModal from "@/components/MessageModal";
import BottomNavigation from "@/components/BottomNavigation";

const mockProducts = [
  {
    id: "1",
    title: "Handgemaakte Biologische Zeep Collectie",
    price: 24.99,
    originalPrice: 32.99,
    description: "Een luxueuze collectie van handgemaakte biologische zepen, vervaardigd met natuurlijke ingrediënten uit België. Elke zeep is met liefde gemaakt door lokale ambachtslieden. Perfect voor de bewuste vrouw die kiest voor duurzame schoonheidsproducten.",
    fullDescription: "Deze unieke collectie bevat vier verschillende zepen, elk met hun eigen unieke geur en eigenschappen. Gemaakt van alleen de beste biologische ingrediënten zoals kokosolie, olijfolie, sheaboter en essentiële oliën. Iedere zeep wordt traditioneel gemaakt en gedroogd gedurende minimaal 6 weken voor de beste kwaliteit.",
    images: [
      "https://images.pexels.com/photos/4465124/pexels-photo-4465124.jpeg?auto=compress&cs=tinysrgb&w=800",
      "https://images.pexels.com/photos/4465831/pexels-photo-4465831.jpeg?auto=compress&cs=tinysrgb&w=800"
    ],
    stock: 8,
    rating: 4.9,
    reviewCount: 23,
    category: "Beauty & Wellness",
    shipping: {
      freeShipping: true,
      estimatedDays: "2-3 werkdagen",
      locations: "België & Nederland",
      pickupAvailable: true,
      pickupLocation: "Gent, België"
    },
    seller: {
      name: "Sophie's Naturals",
      avatar: "https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=200",
      verified: true,
      rating: 4.9,
      location: "Gent, België",
      address: "Korenlei 15, 9000 Gent, België",
      bio: "Gepassioneerd over natuurlijke schoonheidsproducten. Maak al 5 jaar handgemaakte zepen met biologische ingrediënten."
    },
    features: [
      "100% biologische ingrediënten",
      "Handgemaakt in België",
      "Milieuvriendelijke verpakking",
      "4 verschillende geuren",
      "Geschikt voor alle huidtypes",
      "Cruelty-free & vegan"
    ],
    tags: ["Biologisch", "Handgemaakt", "Duurzaam", "Vegan", "Belgian Made"],
    deliveryOptions: [
      {
        type: "shipping",
        title: "Verzending",
        description: "Gratis verzending binnen België",
        price: 0,
        timeframe: "2-3 werkdagen"
      },
      {
        type: "pickup",
        title: "Ophalen",
        description: "Ophalen in Gent centrum",
        price: 0,
        timeframe: "Vandaag beschikbaar",
        location: "Gent, België"
      }
    ]
  }
];

const ProductDetail = () => {
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const { id } = params;
  const product = mockProducts.find(p=>p.id === id);
  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-elegant text-center">
        <h1 className="text-2xl font-serif font-semibold mb-2 text-navy-800">Dit product bestaat niet</h1>
        <p className="text-navy-600 mb-6">Het opgegeven product-ID werd niet gevonden.</p>
        <Button onClick={() => navigate(-1)}>Ga terug</Button>
      </div>
    );
  }

  const handleBuyNow = async () => {
    console.log("Initiating Stripe payment for:", product.title, "Quantity:", quantity);
    // TODO: Integrate with Stripe
    toast.success("Stripe payment integration zou hier worden geactiveerd!");
  };

  const handleSendMessage = () => {
    console.log("Opening message modal for product inquiry");
    setShowMessageModal(true);
  };

  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/marketplace');
    }
  };

  const handleQuantityChange = (change: number) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= product.stock) {
      setQuantity(newQuantity);
    }
  };

  const toggleWishlist = () => {
    setIsWishlisted(!isWishlisted);
    toast.success(isWishlisted ? "Verwijderd van verlanglijst" : "Toegevoegd aan verlanglijst");
  };
  const totalPrice = (product.price * quantity).toFixed(2);

  return (
    <div className="min-h-screen bg-elegant">
      {/* Header with back button */}
      <div className="sticky top-0 z-10 glass-effect border-b border-cream-300">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleGoBack}
              className="btn-elegant-secondary"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span>Terug</span>
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-serif text-navy-800">
                Product Details
              </h1>
              <p className="text-sm text-navy-600">
                Ontdek unieke producten van women on a mission
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleWishlist}
              className="hover:bg-cream-100"
            >
              <Heart className={`h-5 w-5 ${isWishlisted ? 'fill-red-500 text-red-500' : 'text-navy-600'}`} />
            </Button>
          </div>

          {/* Product Header */}
          <div className="card-premium p-4 mb-4">
            <div className="text-center">
              <div className="mb-2">
                <h1 className="script-text text-3xl text-navy-800">
                  Product Details
                </h1>
              </div>
              <p className="text-sm text-navy-600">
                Handgemaakt met liefde door vrouwen in België
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 pb-32">
        {/* Product Image */}
        <div className="relative mb-6 rounded-2xl overflow-hidden shadow-elegant-lg">
          <img 
            src={product.images[0]} 
            alt={product.title}
            className="w-full h-64 md:h-80 object-cover"
            loading="lazy"
            width={800}
            height={320}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          {product.originalPrice > product.price && (
            <Badge className="absolute top-4 left-4 bg-gold-500 text-white">
              Bespaar €{(product.originalPrice - product.price).toFixed(2)}
            </Badge>
          )}
          <div className="absolute top-4 right-4">
            <Badge className="bg-white/90 text-navy-700 hover:bg-white">
              {product.category}
            </Badge>
          </div>
        </div>

        {/* Product Info Card */}
        <Card className="card-premium mb-6">
          <CardHeader className="p-6">
            {/* Seller Info */}
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="h-10 w-10">
                <AvatarImage src={product.seller.avatar} alt={product.seller.name} />
                <AvatarFallback className="bg-cream-200 text-navy-700">
                  {product.seller.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-serif font-semibold text-navy-800">{product.seller.name}</h3>
                  {product.seller.verified && (
                    <Award className="h-4 w-4 text-gold-500" />
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  <span className="text-xs text-navy-600">{product.seller.rating} verkoper rating</span>
                </div>
              </div>
            </div>

            {/* Product Title */}
            <CardTitle className="text-3xl font-serif text-navy-800 mb-2">
              {product.title}
            </CardTitle>

            {/* Rating and Reviews */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-medium text-navy-800">{product.rating}</span>
              </div>
              <span className="text-sm text-navy-600">({product.reviewCount} reviews)</span>
            </div>

            {/* Price Section */}
            <div className="mb-4">
              <div className="flex items-center gap-3">
                <span className="text-4xl font-bold text-navy-800">€{product.price}</span>
                {product.originalPrice > product.price && (
                  <span className="text-lg line-through text-navy-500">€{product.originalPrice}</span>
                )}
              </div>
            </div>

            {/* Stock Check */}
            <div className="mb-6">
              {product.stock > 0 ? (
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">
                    {product.stock} op voorraad
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-red-700">Uitverkocht</span>
                </div>
              )}
            </div>

            <Separator className="my-6 bg-cream-300" />
          </CardHeader>

          <CardContent className="p-6 pt-0">
            {/* Description */}
            <div className="mb-6">
              <CardTitle className="font-serif font-semibold mb-3 text-navy-800">Beschrijving</CardTitle>
              <CardDescription className="leading-relaxed text-navy-700 mb-4">{product.description}</CardDescription>
              <CardDescription className="leading-relaxed text-navy-700">{product.fullDescription}</CardDescription>
            </div>

            {/* Features */}
            <div className="mb-6">
              <CardTitle className="font-serif font-semibold mb-3 text-navy-800">Wat zit erbij:</CardTitle>
              <div className="space-y-2">
                {product.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold-500" />
                    <span className="text-sm text-navy-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div className="mb-6">
              <CardTitle className="font-serif font-semibold mb-3 text-navy-800">Tags:</CardTitle>
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="bg-cream-100 text-navy-700">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
            {/* Shipping Information */}
            <div className="bg-cream-50 rounded-xl p-4 mb-6 border border-cream-200">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="h-4 w-4 text-navy-600" />
                <span className="text-sm font-medium text-navy-700">Bezorging & Ophalen</span>
              </div>
              {/* Delivery Options */}
              <div className="space-y-3 mb-4">
                {product.deliveryOptions.map((option, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-white">
                    <div>
                      <p className="text-sm font-medium text-navy-800">
                        {option.title}
                      </p>
                      <p className="text-xs text-navy-600">{option.description}</p>
                      <p className="text-xs font-medium text-navy-700">{option.timeframe}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-navy-800">
                        {option.price === 0 ? 'Gratis' : `€${option.price}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-xs text-navy-600">
                {product.shipping.locations}
              </div>
            </div>

            {/* Location Preview for Pickup Option */}
            {product.shipping.pickupAvailable && (
              <div className="mb-6">
                <CardTitle className="font-serif font-semibold mb-3 text-navy-800">Ophaallocatie</CardTitle>
                <LocationPreview
                  location={product.seller.location}
                  address={product.seller.address}
                  serviceType="in-person"
                  compact={false}
                />
              </div>
            )}

            {/* Seller Bio */}
            <div className="bg-cream-50 rounded-xl p-4 mb-6 border border-cream-200">
              <CardTitle className="font-serif font-semibold text-navy-800 mb-2">Over {product.seller.name}:</CardTitle>
              <CardDescription className="text-sm text-navy-700 leading-relaxed">{product.seller.bio}</CardDescription>
            </div>
            {/* Woman on a Mission Badge */}
            <div className="bg-gold-50 rounded-lg p-4 border border-gold-200">
              <p className="font-medium text-sm flex items-center gap-2 text-navy-800">
                <div className="gradient-gold text-white px-3 py-1 rounded-full text-xs font-bold">
                  WOM
                </div>
                Woman on a Mission - Geverifieerd Product
                <Shield className="h-4 w-4 text-gold-600" />
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fixed Bottom Purchase Section */}
      <div className="fixed bottom-0 left-0 right-0 glass-effect border-t border-cream-300 p-6 z-50">
        <div className="max-w-md mx-auto">
          {/* Quantity Selector */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-navy-800">Aantal:</span>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
                className="h-8 w-8 p-0 border-cream-300 hover:bg-cream-100"
              >
                -
              </Button>
              <span className="font-medium min-w-[2ch] text-center text-navy-800">{quantity}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuantityChange(1)}
                disabled={quantity >= product.stock}
                className="h-8 w-8 p-0 border-cream-300 hover:bg-cream-100"
              >
                +
              </Button>
            </div>
          </div>

          {/* Total Price */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-serif text-navy-800">Totaal:</span>
            <span className="text-2xl font-bold text-navy-800">€{totalPrice}</span>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleSendMessage}
              variant="outline"
              className="btn-elegant-secondary flex-1 py-4 text-lg font-semibold rounded-xl"
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              Vraag Stellen
            </Button>
            <Button 
              onClick={handleBuyNow}
              disabled={product.stock === 0}
              className="btn-elegant-primary flex-1 py-4 text-lg font-semibold rounded-xl shadow-elegant-lg"
              size="lg"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              {product.stock > 0 ? 'Koop Nu' : 'Uitverkocht'}
            </Button>
          </div>
        </div>
      </div>

      {/* Message Modal */}
      <MessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        recipient={{
          name: product.seller.name,
          avatar: product.seller.avatar,
          verified: product.seller.verified
        }}
        subject={{
          title: product.title,
          type: 'product',
          price: product.price,
          image: product.images[0]
        }}
        onSendMessage={(message) => {
          console.log("Message sent:", message);
          navigate('/messages');
        }}
      />

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default ProductDetail;